import { Grid, GridDirection } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { WbControlError, WbControlHint, WbSelect, WbSkeleton } from '@workbench/react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Direction } from '../../../../utils/enums';
import { ChangeEventHandler, ReactNode } from 'react';
import BeamSelectAsText from './BeamSelectAsText';
import BeamSelectTextLabel from './BeamSelectTextLabel';
import TextBox from '../../dataDisplay/TextBox';
import { TextBoxType } from '../../../../utils/interfaces';

const useStyles = makeStyles()(() => ({
    controlHint: {
        textAlign: 'right',
    },
    horizontal: {
        display: 'flex',
        flexDirection: 'row',
    },
    fullWidth: {
        width: '100%',
    },
    widthAuto: {
        width: 'auto',
        minWidth: '30%',
    },
    skeleton: {
        width: '100%',
        height: '50px',
    },
}));

interface BeamSelectProps {
    label?: string;
    subLabel?: string;
    children?: ReactNode;
    value?: string;
    valueAsText?: string;
    onChange?: ChangeEventHandler<HTMLSelectElement>;
    error?: boolean;
    errorText?: string;
    hint?: boolean;
    hintText?: string;
    hintTextAlignRight?: boolean;
    required?: boolean;
    labelDirection?: Direction;
    showLabelInsideTextField?: boolean;
    compact?: boolean;
    success?: boolean;
    disabled?: boolean;
    className?: string;
    showValueAsText?: boolean;
    fullWidth?: boolean;
    isFetching?: boolean;
    warningInfoBox?: { text: string; textSize?: 'm' | 's' | 'l'; type?: TextBoxType };
    autoWidth?: boolean;
    size?: 's' | 'm' | 'l';
}

const BeamSelect = (props: BeamSelectProps) => {
    const { classes } = useStyles();
    const translate = useTranslation();

    // If showLabelInsideTextField is not defined, the input is compact, else there would be an empty space above the text
    const isCompact =
        props.compact || props.showLabelInsideTextField === undefined || props.showLabelInsideTextField === false;

    // If its compact, it should also be full width automatically
    const isFullWidth = isCompact || props.fullWidth;

    const getGridDirection = (): GridDirection => {
        switch (props.labelDirection) {
            case Direction.LEFT: {
                return 'row';
            }
            case Direction.TOP: {
                return 'column';
            }
            default:
                return 'column';
        }
    };

    const getGridClass = () => {
        switch (props.labelDirection) {
            case Direction.LEFT: {
                return classes.horizontal;
            }
            case Direction.TOP: {
                return '';
            }
            default:
                return '';
        }
    };

    const getDisplayGridSize = () => {
        if (props.label) {
            if (props.labelDirection === Direction.LEFT) {
                return 8;
            }
        }

        return 12;
    };

    const getLabelGridSize = () => {
        if (props.label) {
            if (props.labelDirection === Direction.LEFT) {
                return 4;
            }
        }

        return 12;
    };

    const getClassName = () => {
        let classNames = '';

        if (isFullWidth) {
            classNames += ` ${classes.fullWidth}`;
        }
        if (props.autoWidth) {
            classNames += ` ${classes.widthAuto}`;
        }

        if (props.className) {
            classNames += ` ${props.className}`;
        }

        return classNames.trim();
    };

    if (props.isFetching) {
        return <WbSkeleton className={classes.skeleton}></WbSkeleton>;
    }

    return (
        <Grid container direction={getGridDirection()} className={getGridClass()} size={{ xs: 12 }}>
            {props.showLabelInsideTextField === undefined || props.showLabelInsideTextField === false ? (
                <BeamSelectTextLabel
                    getLabelGridSize={getLabelGridSize}
                    label={props.label}
                    subLabel={props.subLabel}
                    required={props.required}
                />
            ) : null}
            {props.warningInfoBox && (
                <TextBox
                    type={props.warningInfoBox?.type ?? 'warning'}
                    textSize={props.warningInfoBox?.textSize}
                    text={props.warningInfoBox?.text || translate('warning')}
                />
            )}
            <Grid size={{ xs: getDisplayGridSize() }}>
                {props.showValueAsText ? (
                    <BeamSelectAsText
                        showValueAsText={props.showValueAsText}
                        value={props.valueAsText ?? props.value}
                    />
                ) : (
                    <WbSelect
                        compact={isCompact ? true : undefined}
                        success={props.success}
                        className={getClassName()}
                        size={props.size ?? 'l'}
                    >
                        <span slot="label">{props.showLabelInsideTextField && props.label ? props.label : ''}</span>
                        <select onChange={props.onChange} value={props.value ?? ''} disabled={props.disabled}>
                            {props.children}
                        </select>
                        {props.error && (
                            <WbControlError slot="message">
                                {props.errorText ?? translate('mandatory_field')}
                            </WbControlError>
                        )}
                        {props.hint && (
                            <WbControlHint
                                slot="message"
                                className={props.hintTextAlignRight ? classes.controlHint : ''}
                            >
                                {props.hintText}
                            </WbControlHint>
                        )}
                    </WbSelect>
                )}
            </Grid>
        </Grid>
    );
};

export default BeamSelect;
